import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ApiService } from '../services/api.service';
import { CommonService } from '../services/common.service';
import { UtilitiesService } from '../services/utilities.service';

type IdState = 'ok' | 'expiring' | 'expired' | 'missing';

interface EmployeeRecord {
  id: string;
  name: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  role_id: string | null;
  active: boolean | number | string | null;
  address: string | null;
  country: string | null;
  account_number: string | null;
  bank_name: string | null;
  basic_salary: string | number | null;
  birth_date: string | null;
  gender: string | null;
  id_number: string | null;
  id_expiry_date: string | null;
  id_attachment: unknown;
  leave_balance: string | number | null;
  sick_balance: string | number | null;
  hours_balance: string | number | null;
  status: string | null;
  status_from_date: string | null;
  status_to_date: string | null;
  user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  hidden: boolean;
  idState: IdState;
  expiryDays: number | null;
}

interface EmployeeForm {
  name: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  account_number: string;
  bank_name: string;
  id_number: string;
  id_expiry_date: string;
  basic_salary: number | null;
  gender: string;
  birth_date: string;
  user_id: string | null;
}

@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.css'],
  standalone: false
})
export class EmployeesComponent implements OnInit, OnDestroy {
  private readonly idWarningDays = 60;
  private querySub?: Subscription;

  @ViewChild('editModal') editModal?: TemplateRef<unknown>;
  @ViewChild('statusModal') statusModal?: TemplateRef<unknown>;

  page = 1;
  pageSize = 10;
  token: string | null;
  employees: EmployeeRecord[] = [];
  loadError = false;
  saving = false;
  focusedUserId: string | null = null;

  filtersOpen = false;
  searchTerm = '';
  roleFilter = 'all';
  statusFilter = 'all';
  idFilter = 'all';
  accountFilter = 'all';
  sortBy: 'name' | 'salary' | 'expiry' | 'leave' = 'name';

  selected: EmployeeRecord | null = null;
  editingId: string | null = null;
  form: EmployeeForm = this.emptyForm();
  statusForm = {
    status: '',
    status_from_date: '',
    status_to_date: ''
  };

  readonly statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'LEAVE', label: 'On leave' },
    { value: 'TERMINATED', label: 'Terminated' },
    { value: 'ESCAPE', label: 'Escape' }
  ];

  constructor(
    public utility: UtilitiesService,
    private modalService: NgbModal,
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private common: CommonService
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    this.querySub = this.route.queryParams.subscribe(params => {
      const id = params['id'];
      this.focusedUserId = id != null && id !== '' ? String(id) : null;
      this.page = 1;
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
  }

  get scopedEmployees(): EmployeeRecord[] {
    if (!this.focusedUserId) {
      return this.employees;
    }
    return this.employees.filter(item => item.user_id === this.focusedUserId);
  }

  get counts() {
    const rows = this.scopedEmployees;
    return {
      all: rows.length,
      active: rows.filter(item => item.status === 'ACTIVE').length,
      leave: rows.filter(item => item.status === 'LEAVE').length,
      idAlert: rows.filter(item => item.idState === 'expired' || item.idState === 'expiring').length
    };
  }

  get roleOptions(): string[] {
    return [...new Set(this.scopedEmployees.map(item => item.role).filter((role): role is string => !!role))].sort();
  }

  get statusChoices(): { value: string; label: string }[] {
    const known = new Set(this.statusOptions.map(option => option.value));
    const extra = [...new Set(
      this.employees
        .map(item => item.status)
        .filter((status): status is string => !!status && !known.has(status))
    )];
    return [
      ...this.statusOptions,
      ...extra.map(value => ({ value, label: this.statusMeta(value).label }))
    ];
  }

  get filteredItems(): EmployeeRecord[] {
    const query = this.searchTerm.trim().toLowerCase();
    const rows = this.scopedEmployees.filter(item => {
      if (query && !this.matchesSearch(item, query)) {
        return false;
      }
      if (this.roleFilter !== 'all' && item.role !== this.roleFilter) {
        return false;
      }
      if (this.statusFilter !== 'all' && item.status !== this.statusFilter) {
        return false;
      }
      if (this.accountFilter === 'active' && !this.isAccountActive(item)) {
        return false;
      }
      if (this.accountFilter === 'disabled' && !this.isAccountDisabled(item)) {
        return false;
      }
      if (this.idFilter === 'attention' && item.idState !== 'expired' && item.idState !== 'expiring') {
        return false;
      }
      if (this.idFilter === 'expired' && item.idState !== 'expired') {
        return false;
      }
      if (this.idFilter === 'expiring' && item.idState !== 'expiring') {
        return false;
      }
      if (this.idFilter === 'missing' && item.idState !== 'missing') {
        return false;
      }
      return true;
    });

    return [...rows].sort((a, b) => this.compare(a, b));
  }

  get paginatedItems(): EmployeeRecord[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  load(): void {
    this.utility.loading = true;
    this.loadError = false;
    this.api.get('employees/', this.token).subscribe({
      next: (data: unknown) => {
        this.employees = this.asList(data)
          .map(row => this.normalize(row))
          .filter(item => item.id && !item.hidden);
        const maxPage = Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
        if (this.page > maxPage) {
          this.page = 1;
        }
      },
      error: () => {
        this.employees = [];
        this.loadError = true;
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.searchTerm.trim()) count++;
    if (this.roleFilter !== 'all') count++;
    if (this.statusFilter !== 'all') count++;
    if (this.idFilter !== 'all') count++;
    if (this.accountFilter !== 'all') count++;
    if (this.sortBy !== 'name') count++;
    return count;
  }

  get filterSummary(): string {
    const parts: string[] = [];
    const search = this.searchTerm.trim();
    if (search) parts.push(search);
    if (this.roleFilter !== 'all') {
      parts.push(this.roleFilter.charAt(0).toUpperCase() + this.roleFilter.slice(1));
    }
    if (this.statusFilter !== 'all') parts.push(this.statusMeta(this.statusFilter).label);
    if (this.idFilter === 'attention') parts.push('ID expiring or expired');
    if (this.idFilter === 'expiring') parts.push('ID within 60 days');
    if (this.idFilter === 'expired') parts.push('ID expired');
    if (this.idFilter === 'missing') parts.push('No ID expiry');
    if (this.accountFilter === 'active') parts.push('Login active');
    if (this.accountFilter === 'disabled') parts.push('Login disabled');
    if (this.sortBy === 'salary') parts.push('Highest salary');
    if (this.sortBy === 'expiry') parts.push('Soonest ID expiry');
    if (this.sortBy === 'leave') parts.push('Lowest annual balance');
    return parts.join(' · ');
  }

  onFilterChange(): void {
    this.page = 1;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.roleFilter = 'all';
    this.statusFilter = 'all';
    this.idFilter = 'all';
    this.accountFilter = 'all';
    this.sortBy = 'name';
    this.page = 1;
  }

  applyQuick(kind: 'all' | 'active' | 'leave' | 'id'): void {
    this.searchTerm = '';
    this.roleFilter = 'all';
    this.accountFilter = 'all';
    this.sortBy = 'name';
    if (kind === 'active') {
      this.statusFilter = 'ACTIVE';
      this.idFilter = 'all';
    } else if (kind === 'leave') {
      this.statusFilter = 'LEAVE';
      this.idFilter = 'all';
    } else if (kind === 'id') {
      this.statusFilter = 'all';
      this.idFilter = 'attention';
    } else {
      this.statusFilter = 'all';
      this.idFilter = 'all';
    }
    this.page = 1;
  }

  isQuick(kind: 'all' | 'active' | 'leave' | 'id'): boolean {
    if (this.searchTerm.trim() || this.roleFilter !== 'all' || this.accountFilter !== 'all') {
      return false;
    }
    if (kind === 'all') {
      return this.statusFilter === 'all' && this.idFilter === 'all';
    }
    if (kind === 'active') {
      return this.statusFilter === 'ACTIVE' && this.idFilter === 'all';
    }
    if (kind === 'leave') {
      return this.statusFilter === 'LEAVE' && this.idFilter === 'all';
    }
    return this.statusFilter === 'all' && this.idFilter === 'attention';
  }

  clearFocus(): void {
    this.router.navigate(['/employees']);
  }

  openDetails(content: TemplateRef<unknown>, item: EmployeeRecord): void {
    this.selected = item;
    this.modalService.open(content, { windowClass: 'employee-details-modal', scrollable: true });
  }

  openEdit(content: TemplateRef<unknown>, item: EmployeeRecord): void {
    this.selected = item;
    this.editingId = item.id;
    this.saving = false;
    const gender = (item.gender ?? '').toLowerCase();
    this.form = {
      name: item.name ?? '',
      email: item.email ?? '',
      phone: item.phone ?? '',
      address: item.address ?? '',
      country: item.country ?? '',
      account_number: item.account_number ?? '',
      bank_name: item.bank_name ?? '',
      id_number: item.id_number ?? '',
      id_expiry_date: this.toDateInput(item.id_expiry_date),
      basic_salary: this.amountOrNull(item.basic_salary),
      gender: gender === 'male' || gender === 'female' ? gender : (item.gender || '0'),
      birth_date: this.toDateInput(item.birth_date),
      user_id: item.user_id
    };
    this.modalService.open(content, { size: 'lg', scrollable: true });
  }

  openStatus(content: TemplateRef<unknown>, item: EmployeeRecord): void {
    this.selected = item;
    this.editingId = item.id;
    this.saving = false;
    this.statusForm = {
      status: item.status || '',
      status_from_date: this.toDateInput(item.status_from_date),
      status_to_date: this.toDateInput(item.status_to_date)
    };
    this.modalService.open(content, { scrollable: true });
  }

  editFromDetails(): void {
    this.openFromDetails(this.editModal, item => this.openEdit(this.editModal!, item));
  }

  statusFromDetails(): void {
    this.openFromDetails(this.statusModal, item => this.openStatus(this.statusModal!, item));
  }

  saveEmployee(): void {
    if (!this.editingId) {
      return;
    }
    const name = this.form.name.trim();
    if (!name) {
      this.warn('Enter the employee name.');
      return;
    }

    const body = {
      name,
      email: this.emptyToNull(this.form.email),
      phone: this.emptyToNull(this.form.phone),
      address: this.emptyToNull(this.form.address),
      country: this.emptyToNull(this.form.country),
      account_number: this.emptyToNull(this.form.account_number),
      bank_name: this.emptyToNull(this.form.bank_name),
      id_number: this.emptyToNull(this.form.id_number),
      id_expiry_date: this.form.id_expiry_date || null,
      basic_salary: this.form.basic_salary,
      gender: this.form.gender === '0' ? null : this.form.gender,
      birth_date: this.form.birth_date || null,
      user_id: this.form.user_id
    };

    this.saving = true;
    this.api.update('employees/' + this.editingId, body, this.token).subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.common.getDoctors();
        this.load();
        Swal.fire({
          title: 'Success!',
          text: 'Employee updated.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      },
      error: (error) => this.failed(error),
      complete: () => {
        this.saving = false;
      }
    });
  }

  saveStatus(): void {
    if (!this.editingId) {
      return;
    }
    if (!this.statusForm.status) {
      this.warn('Choose a status.');
      return;
    }
    if (this.statusForm.status === 'LEAVE') {
      if (!this.statusForm.status_from_date || !this.statusForm.status_to_date) {
        this.warn('Enter the leave start and end dates.');
        return;
      }
      if (this.statusForm.status_to_date < this.statusForm.status_from_date) {
        this.warn('The end date must be on or after the start date.');
        return;
      }
    }

    const body = {
      status: this.statusForm.status,
      status_from_date: this.statusForm.status === 'LEAVE' ? this.statusForm.status_from_date : null,
      status_to_date: this.statusForm.status === 'LEAVE' ? this.statusForm.status_to_date : null
    };

    this.saving = true;
    this.api.update(`employees/${this.editingId}/status`, body, this.token).subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.load();
        Swal.fire({
          title: 'Success!',
          text: 'Status updated.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      },
      error: (error) => this.failed(error),
      complete: () => {
        this.saving = false;
      }
    });
  }

  resetBalance(item: EmployeeRecord): void {
    const annual = this.displayAmount(item.leave_balance);
    const sick = this.displayAmount(item.sick_balance);
    Swal.fire({
      title: `Reset leave for ${item.name}?`,
      text: `Annual balance becomes 30 and sick balance becomes 10. Current balances are annual ${annual} and sick ${sick}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Reset balances',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.api.update(`leaves/balance/${item.id}/ANNUAL`, { leaves: 30 }, this.token).subscribe({
        next: () => {
          this.api.update(`leaves/balance/${item.id}/SICK`, { leaves: 10 }, this.token).subscribe({
            next: () => {
              Swal.fire({
                title: 'Success!',
                text: 'Leave balance reset.',
                icon: 'success',
                confirmButtonText: 'OK'
              });
              this.load();
            },
            error: (error) => {
              this.failed(error);
              this.load();
            }
          });
        },
        error: (error) => this.failed(error)
      });
    });
  }

  exportCsv(): void {
    const rows = this.filteredItems;
    if (!rows.length) {
      return;
    }
    const headers = [
      'Name', 'Username', 'Role', 'Status', 'Login', 'Phone', 'Email',
      'ID number', 'ID expiry', 'Country', 'Bank', 'Account number',
      'Basic salary', 'Annual balance', 'Sick balance', 'Hours balance',
      'Status from', 'Status to'
    ];
    const lines = [
      headers,
      ...rows.map(item => [
        item.name,
        item.username,
        item.role,
        item.status,
        this.isAccountDisabled(item) ? 'Disabled' : (this.isAccountActive(item) ? 'Active' : ''),
        item.phone,
        item.email,
        item.id_number,
        this.displayDate(item.id_expiry_date) === '—' ? '' : this.displayDate(item.id_expiry_date),
        item.country,
        item.bank_name,
        item.account_number,
        item.basic_salary,
        item.leave_balance,
        item.sick_balance,
        item.hours_balance,
        this.displayDate(item.status_from_date) === '—' ? '' : this.displayDate(item.status_from_date),
        this.displayDate(item.status_to_date) === '—' ? '' : this.displayDate(item.status_to_date)
      ])
    ];
    const csv = lines.map(line => line.map(cell => this.csvCell(cell)).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date();
    const stamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    link.href = url;
    link.download = `employees-${stamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  statusMeta(status: string | null): { label: string; badge: string } {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':
        return { label: 'Active', badge: 'bg-success' };
      case 'INACTIVE':
        return { label: 'Inactive', badge: 'bg-secondary' };
      case 'LEAVE':
        return { label: 'On leave', badge: 'bg-dark' };
      case 'TERMINATED':
        return { label: 'Terminated', badge: 'bg-danger' };
      case 'ESCAPE':
        return { label: 'Escape', badge: 'bg-warning text-dark' };
      default:
        return { label: status || 'Unknown', badge: 'bg-light text-dark' };
    }
  }

  roleBadge(role: string | null): string {
    switch ((role || '').toLowerCase()) {
      case 'doctor':
        return 'bg-dark';
      case 'staff':
        return 'bg-info text-dark';
      case 'accountant':
        return 'bg-primary';
      case 'admin':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  }

  isAccountActive(item: EmployeeRecord): boolean {
    return item.active === true || item.active === 1 || item.active === '1';
  }

  isAccountDisabled(item: EmployeeRecord): boolean {
    return item.active === false || item.active === 0 || item.active === '0';
  }

  amount(value: string | number | null): number {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }

  displayAmount(value: string | number | null): string {
    if (value == null || value === '') {
      return '—';
    }
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return '—';
    }
    return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
  }

  displayDate(value: string | null): string {
    const input = this.toDateInput(value);
    if (!input) {
      return '—';
    }
    const [year, month, day] = input.split('-');
    return `${day}/${month}/${year}`;
  }

  expiryText(item: EmployeeRecord): string | null {
    if (item.expiryDays == null || item.idState === 'ok' || item.idState === 'missing') {
      return null;
    }
    if (item.idState === 'expired') {
      const days = Math.abs(item.expiryDays);
      return days === 0 ? 'Expired today' : `Expired ${days} day(s) ago`;
    }
    if (item.expiryDays === 0) {
      return 'Expires today';
    }
    return `Expires in ${item.expiryDays} day(s)`;
  }

  genderLabel(value: string | null): string {
    if (!value || value === '0') {
      return '—';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  attachmentUrl(value: unknown): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'object') {
      const file = value as { url?: unknown; path?: unknown; file?: unknown };
      const url = file.url || file.path || file.file;
      if (typeof url === 'string' && url.trim()) {
        return url.trim();
      }
    }
    return null;
  }

  private isHiddenFlag(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private openFromDetails(template: TemplateRef<unknown> | undefined, open: (item: EmployeeRecord) => void): void {
    const item = this.selected;
    if (!item || !template) {
      return;
    }
    this.modalService.dismissAll();
    setTimeout(() => open(item));
  }

  private matchesSearch(item: EmployeeRecord, query: string): boolean {
    return [
      item.name, item.username, item.email, item.phone, item.id_number,
      item.country, item.bank_name, item.role, item.status
    ].filter(Boolean).join(' ').toLowerCase().includes(query);
  }

  private compare(a: EmployeeRecord, b: EmployeeRecord): number {
    if (this.sortBy === 'salary') {
      return this.amount(b.basic_salary) - this.amount(a.basic_salary);
    }
    if (this.sortBy === 'leave') {
      return this.amount(a.leave_balance) - this.amount(b.leave_balance);
    }
    if (this.sortBy === 'expiry') {
      return (a.expiryDays ?? 99999) - (b.expiryDays ?? 99999);
    }
    return a.name.localeCompare(b.name);
  }

  private asList(data: unknown): any[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object' && 'id' in data && 'name' in data) {
      return [data];
    }
    return [];
  }

  private normalize(row: any): EmployeeRecord {
    const source = row?.employee ?? row ?? {};
    const user = row?.user ?? {};
    const record: EmployeeRecord = {
      id: source.id != null ? String(source.id) : '',
      name: source.name ?? '',
      username: source.username ?? user.username ?? null,
      email: source.email ?? user.email ?? null,
      phone: source.phone != null ? String(source.phone) : (user.phone != null ? String(user.phone) : null),
      role: source.role ?? user.role ?? null,
      role_id: source.role_id ?? user.role_id ?? null,
      active: source.active ?? user.active ?? null,
      address: source.address ?? null,
      country: source.country ?? null,
      account_number: source.account_number != null ? String(source.account_number) : null,
      bank_name: source.bank_name ?? null,
      basic_salary: source.basic_salary ?? null,
      birth_date: source.birth_date ?? null,
      gender: source.gender ?? null,
      id_number: source.id_number != null ? String(source.id_number) : null,
      id_expiry_date: source.id_expiry_date ?? null,
      id_attachment: source.id_attachment ?? null,
      leave_balance: source.leave_balance ?? null,
      sick_balance: source.sick_balance ?? null,
      hours_balance: source.hours_balance ?? null,
      status: source.status ?? null,
      status_from_date: source.status_from_date ?? null,
      status_to_date: source.status_to_date ?? null,
      user_id: source.user_id != null ? String(source.user_id) : (user.id != null ? String(user.id) : null),
      created_at: source.created_at ?? null,
      updated_at: source.updated_at ?? null,
      hidden: this.isHiddenFlag(source.hidden),
      idState: 'missing',
      expiryDays: null
    };
    return this.decorateId(record);
  }

  private decorateId(record: EmployeeRecord): EmployeeRecord {
    const expiry = this.toDateInput(record.id_expiry_date);
    if (!expiry) {
      record.idState = 'missing';
      record.expiryDays = null;
      return record;
    }
    const [year, month, day] = expiry.split('-').map(Number);
    const expiryUtc = Date.UTC(year, month - 1, day);
    const now = new Date();
    const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((expiryUtc - todayUtc) / 86400000);
    record.expiryDays = days;
    if (days < 0) {
      record.idState = 'expired';
    } else if (days <= this.idWarningDays) {
      record.idState = 'expiring';
    } else {
      record.idState = 'ok';
    }
    return record;
  }

  private toDateInput(value: string | null): string {
    if (!value) {
      return '';
    }
    const text = String(value).trim();
    const plain = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (plain) {
      return `${plain[1]}-${plain[2]}-${plain[3]}`;
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${date.getUTCFullYear()}-${month}-${day}`;
  }

  private amountOrNull(value: string | number | null): number | null {
    if (value == null || value === '') {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private emptyToNull(value: string | null): string | null {
    const text = (value ?? '').trim();
    return text ? text : null;
  }

  private emptyForm(): EmployeeForm {
    return {
      name: '',
      email: '',
      phone: '',
      address: '',
      country: '',
      account_number: '',
      bank_name: '',
      id_number: '',
      id_expiry_date: '',
      basic_salary: null,
      gender: '0',
      birth_date: '',
      user_id: null
    };
  }

  private csvCell(value: unknown): string {
    const text = value == null ? '' : String(value);
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private warn(text: string): void {
    Swal.fire({
      title: 'Check the form',
      text,
      icon: 'warning',
      confirmButtonText: 'OK'
    });
  }

  private failed(error: any): void {
    const body = error?.error;
    let text = 'Something went wrong.';
    if (typeof body === 'string' && body.trim()) {
      text = body;
    } else if (typeof body?.message === 'string') {
      text = body.message;
    } else if (typeof body?.error === 'string') {
      text = body.error;
    }
    Swal.fire({
      title: 'Error!',
      text,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }
}
